import { LocationDetail } from '../models/Location';
import { getLocationObjectKey, getPointType } from './locationBrowse';
import {
    CATEGORY_RULES, GENERIC_INSTITUTION_NAMES, INSTITUTION_UNIT_PATTERNS, NAME_CORRECTIONS,
    NAME_PATTERNS, STAT_CATEGORIES, StatCategoryId, StatGroupId,
} from './locationStatisticsRules';

export { STAT_CATEGORIES } from './locationStatisticsRules';
export type { StatCategoryId, StatGroupId } from './locationStatisticsRules';

export interface StatsCounts { institutions: number | null; objects: number; points: number; }
export interface StatsCategory extends StatsCounts { id: StatCategoryId; label: string; }
export interface StatsGroup extends StatsCounts { id: StatGroupId; label: string; categories: StatsCategory[]; }
export interface LocationStatistics {
    totals: { points: number; objects: number; cities: number };
    groups: StatsGroup[];
    remaining: StatsGroup;
}
interface Classification { category: StatCategoryId; institution: string | null; }
interface InstitutionName { name: string; normalizedName: string; city: string; }
type UnitGroup = keyof typeof INSTITUTION_UNIT_PATTERNS;
type InstitutionClassifier = (context: InstitutionName) => Classification | null;

const clean = (value: unknown): string => String(value ?? '').normalize('NFC').trim().replace(/\s+/g, ' ').replace(/[ ,;]+$/, '');
const fold = (value: unknown): string => clean(value).toLocaleLowerCase('pl').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ł/g, 'l');
const matchesAny = (name: string, patterns: readonly RegExp[]): boolean => patterns.some(pattern => pattern.test(name));
const withoutInstitution = (category: 'review' | 'other'): Classification => ({ category, institution: null });
const higherEducationCategories = new Set<StatCategoryId>(CATEGORY_RULES.higherEducation.map(rule => rule.category));
const unitPatterns = new Map<UnitGroup, RegExp>();

function findCategoryRule<T extends { names: readonly RegExp[] }>(name: string, rules: readonly T[]): T | undefined {
    return rules.find(rule => matchesAny(name, rule.names));
}

function normalizeInstitutionName(value: string): string {
    const name = NAME_CORRECTIONS.reduce((current, rule) => current.replace(rule.pattern, rule.replacement), clean(value));
    const duplicated = name.match(/^(.{8,}?)\s*\1$/iu);
    return duplicated ? duplicated[1] : name;
}

function cutAtUnit(name: string, group: UnitGroup): string {
    let pattern = unitPatterns.get(group);
    if (!pattern) {
        const units = INSTITUTION_UNIT_PATTERNS[group].map(unit => unit.source).join('|');
        pattern = new RegExp(`(?:,\\s*|\\s+[-–—]\\s+|\\s+)(?=(?:${units})(?:\\s|,|$))`, 'iu');
        unitPatterns.set(group, pattern);
    }
    const index = name.search(pattern);
    return clean(index > 0 ? name.slice(0, index) : name);
}

function classifyExcluded({ normalizedName }: InstitutionName): Classification | null {
    return matchesAny(normalizedName, NAME_PATTERNS.excluded) ? withoutInstitution('other') : null;
}

function classifyZus({ name, normalizedName }: InstitutionName): Classification | null {
    if (!matchesAny(normalizedName, NAME_PATTERNS.zus)) return null;

    const rule = findCategoryRule(normalizedName, CATEGORY_RULES.zus);
    const unit = rule ? name.slice(name.search(rule.institutionStart)).split(',')[0] : name;
    const institution = 'ZUS ' + cutAtUnit(unit, 'zus').replace(/\s+(?:w|we)\s+/giu, ' ');
    return { category: rule?.category ?? 'zusOther', institution };
}

function normalizeUniversityName({ name, normalizedName }: InstitutionName): string {
    if (/uniwersytetu\b/.test(normalizedName)) {
        return name.slice(normalizedName.indexOf('uniwersytetu')).replace(/^uniwersytetu /iu, 'Uniwersytet ')
            .replace(/^(Uniwersytet )([^ ]*?)kiego\b/iu, '$1$2ki')
            .replace(/^(Uniwersytet )([^ ]*?)ego\b/iu, '$1$2y');
    }
    if (/^(biblioteka|centrum)\b/.test(normalizedName)) {
        return name.slice(normalizedName.indexOf('uniwersytet'));
    }
    return name;
}

const higherEducationNormalizers: Partial<Record<StatCategoryId, (context: InstitutionName) => string>> = {
    university: normalizeUniversityName,
    higherSchool: ({ name }) => name.replace(/^Dom Studenta Szkoły Wyższej/iu, 'Szkoła Wyższa'),
};

function classifyHigherEducation(context: InstitutionName): Classification | null {
    const rule = findCategoryRule(context.normalizedName, CATEGORY_RULES.higherEducation);
    if (!rule) return null;

    const normalize = higherEducationNormalizers[rule.category];
    const institution = cutAtUnit(normalize ? normalize(context) : context.name, 'higherEducation');
    if (GENERIC_INSTITUTION_NAMES.higherEducation.includes(fold(institution))) return withoutInstitution('review');
    return { category: rule.category, institution };
}

function classifyAmbiguousPublicInstitution({ normalizedName }: InstitutionName): Classification | null {
    const incompleteEducation = matchesAny(normalizedName, NAME_PATTERNS.incompleteEducation);
    const conflictingCounty = matchesAny(normalizedName, NAME_PATTERNS.county)
        && matchesAny(normalizedName, NAME_PATTERNS.conflictingCounty);
    const ambiguousPublicInstitution = matchesAny(normalizedName, NAME_PATTERNS.ambiguousPublicInstitution);
    return incompleteEducation || conflictingCounty || ambiguousPublicInstitution ? withoutInstitution('review') : null;
}

function classifyAdministration({ name, normalizedName, city }: InstitutionName): Classification | null {
    const rule = findCategoryRule(normalizedName, CATEGORY_RULES.administration);
    if (!rule) return null;

    const institution = cutAtUnit(name.replace(/^Budynek Urzędu /iu, 'Urząd '), 'administration')
        .replace(/\s*\(wejście\).*$/iu, '').replace(/\s+Oddział Rodzinny.*$/iu, '');
    const normalizedInstitution = fold(institution);
    if (matchesAny(normalizedInstitution, NAME_PATTERNS.ambiguousAdministration)) return withoutInstitution('review');

    const needsCity = GENERIC_INSTITUTION_NAMES.administration.includes(normalizedInstitution);
    if (needsCity && !city) return withoutInstitution('review');
    return { category: rule.category, institution: needsCity ? `${institution} ${city}` : institution };
}

function normalizeLibraryName(name: string): string {
    const institution = name.replace(/^Filia Biblioteki Publicznej /iu, 'Biblioteka Publiczna ');
    return cutAtUnit(institution, 'library').split(/,\s*(?=biblioteka\b)/iu)[0];
}

const cultureNormalizers: Partial<Record<StatCategoryId, (name: string) => string>> = {
    library: normalizeLibraryName,
    theater: name => cutAtUnit(name, 'theater').replace(/\s*\(wejście\).*$/iu, ''),
};

function classifyCulture({ name, normalizedName, city }: InstitutionName): Classification | null {
    const matchingRules = CATEGORY_RULES.culture.filter(rule => matchesAny(normalizedName, rule.names));
    const categories = matchingRules.map(rule => rule.category);
    if (categories.includes('library') && categories.includes('culture')) return withoutInstitution('review');

    const rule = matchingRules[0];
    if (!rule) return null;
    if (rule.category === 'museum' && matchesAny(normalizedName, NAME_PATTERNS.museumBranch)) return withoutInstitution('review');

    const normalize = cultureNormalizers[rule.category];
    const institution = normalize ? normalize(name) : cutAtUnit(name, rule.category);
    const libraryWithoutCity = rule.category === 'library' && !city
        && GENERIC_INSTITUTION_NAMES.library.includes(fold(institution));
    if (libraryWithoutCity) return withoutInstitution('review');
    return { category: rule.category, institution };
}

function classifySchool({ name, normalizedName }: InstitutionName): Classification | null {
    if (!matchesAny(normalizedName, NAME_PATTERNS.school)) return null;
    return { category: 'school', institution: cutAtUnit(name, 'school') };
}

function classifyOtherRequiringReview({ normalizedName }: InstitutionName): Classification | null {
    return matchesAny(normalizedName, NAME_PATTERNS.otherRequiringReview) ? withoutInstitution('review') : null;
}

const CLASSIFIERS_IN_PRIORITY_ORDER: readonly InstitutionClassifier[] = [
    classifyExcluded,
    classifyZus,
    classifyHigherEducation,
    classifyAmbiguousPublicInstitution,
    classifyAdministration,
    classifyCulture,
    classifySchool,
    classifyOtherRequiringReview,
];

export function classifyInstitution(point: LocationDetail): Classification {
    const name = normalizeInstitutionName(point.name);
    const context = { name, normalizedName: fold(name), city: clean(point.city) };

    for (const classify of CLASSIFIERS_IN_PRIORITY_ORDER) {
        const classification = classify(context);
        if (classification) return classification;
    }
    return withoutInstitution('other');
}

function institutionKey(classification: Classification, point: LocationDetail): string | null {
    if (!classification.institution) return null;
    const name = fold(classification.institution).replace(/\s+(?:w|we)\s+/g, ' ');
    const city = fold(point.city);
    return JSON.stringify([classification.category, name, higherEducationCategories.has(classification.category) ? '' : city]);
}

export function getLocationStatistics(locations: readonly LocationDetail[]): LocationStatistics {
    const seen = new Set<string>();
    const objects = new Set<string>();
    const cities = new Set<string>();
    const classifications = new Map<string, Classification>();
    const rows = STAT_CATEGORIES.map(config => ({ ...config, institutions: new Set<string>(), objects: new Set<string>(), points: 0 }));
    const byCategory = new Map(rows.map(row => [row.id, row]));
    const assigned: { point: LocationDetail; classification: Classification }[] = [];
    for (const point of locations) {
        const id = point.id?.trim().toLocaleLowerCase('pl');
        if (!id || !getPointType(id) || seen.has(id) || ![1, '1'].includes(point.share) || !clean(point.name) || point.name.includes('^')) continue;
        seen.add(id);
        const cacheKey = JSON.stringify([point.name, point.city]);
        let classification = classifications.get(cacheKey);
        if (!classification) {
            classification = classifyInstitution(point);
            classifications.set(cacheKey, classification);
        }
        assigned.push({ point, classification });
    }
    const roots = new Map<string, { category: StatCategoryId; name: string; cities: Set<string> }>();
    for (const { point, classification } of assigned) {
        if (!higherEducationCategories.has(classification.category) || !classification.institution) continue;
        const key = classification.category + ':' + fold(classification.institution);
        if (!roots.has(key)) roots.set(key, { category: classification.category, name: classification.institution, cities: new Set() });
        roots.get(key)!.cities.add(fold(point.city));
    }
    const resolved = new Map<string, string | null>();
    for (const { point, classification } of assigned) {
        const cacheKey = JSON.stringify([classification.category, classification.institution, point.city]);
        if (!resolved.has(cacheKey)) {
            let identity = classification;
            if (higherEducationCategories.has(classification.category) && classification.institution) {
                const candidates = Array.from(roots.values()).filter(root => root.category === classification.category
                    && (fold(classification.institution).startsWith(fold(root.name) + ' w ') || fold(classification.institution).startsWith(fold(root.name) + ' we '))
                    && (root.cities.has(fold(point.city)) || (root.category === 'polytechnic' && /(?:ska|cka)$/.test(fold(root.name)))));
                if (candidates.length === 1) identity = { ...classification, institution: candidates[0].name };
            }
            resolved.set(cacheKey, institutionKey(identity, point));
        }
        const row = byCategory.get(classification.category)!;
        const object = getLocationObjectKey(point);
        row.points++;
        row.objects.add(object);
        const institution = resolved.get(cacheKey);
        if (institution) row.institutions.add(institution);
        objects.add(object);
        if (clean(point.city)) cities.add(fold(point.city));
    }
    const group = (id: StatGroupId, label: string): StatsGroup => {
        const selected = rows.filter(row => row.group === id);
        return { id, label,
            institutions: id === 'other' ? null : new Set(selected.flatMap(row => Array.from(row.institutions))).size,
            objects: new Set(selected.flatMap(row => Array.from(row.objects))).size,
            points: selected.reduce((sum, row) => sum + row.points, 0),
            categories: selected.map(row => ({ id: row.id, label: row.label, institutions: id === 'other' ? null : row.institutions.size, objects: row.objects.size, points: row.points })),
        };
    };
    return { totals: { points: seen.size, objects: objects.size, cities: cities.size },
        groups: [group('administration', 'Administracja'), group('culture', 'Kultura'), group('education', 'Edukacja'), group('zus', 'ZUS')],
        remaining: group('other', 'Pozostałe lokalizacje'),
    };
}
